/**
 * @file Module to create Dialog component.
 */

/**
 * Class representing a dialog.
 */
export class Dialog {

  /**
   * Setup properties.
   * @param {HTMLElement} element - The DOM element to append the component.
   */
  constructor(element) {
    this.dom = {
      dialog: element,
      content: element.querySelector('.content'),
      id: element.id,
      closeButtons: [],
      focusableElements: []
    };
    this.type = element.dataset.type;
    this.trigger = element.dataset.trigger;
    this.events = {
      closeDialogEvent: new CustomEvent('DialogClose', {
        detail: { id: this.dom.id }
      }),
      showDialogEvent: new CustomEvent('DialogShow', {
        detail: { id: this.dom.id }
      })
    };
    this.listeners = {
      show: () => this.show(),
      hide: () => this.hide(),
      triggerDialog: (evt) => this._triggerDialog(evt),
      keydown: (evt) => {
        // Close dialog when press ESC, but not when on <input>
        if (evt.key === 'Escape' &&
          this.dom.dialog.classList.contains('show') &&
          evt.target.tagName.toUpperCase() !== 'INPUT') {
          this.hide();
        }

        /**
         * Trap focus inside modal.
         * Explanation: https://www.w3.org/TR/wai-aria-practices-1.1/#keyboard-interaction-7
         * Issues: this may not properly work if iframe is the last element in modal.
         */
        if (evt.key === 'Tab') {
          const firstFocusableElement = this.dom.focusableElements[0];
          const lastFocusableElement = this.dom.focusableElements[this.dom.focusableElements.length - 1];
          // if shift + tab combination pressed
          if (evt.shiftKey) {
            if (evt.target === firstFocusableElement) {
              lastFocusableElement.focus();
              evt.preventDefault();
            }
          } else {
            // if focused has reached to last focusable element then focus first focusable element after pressing tab
            if (evt.target === lastFocusableElement) {
              firstFocusableElement.focus();
              evt.preventDefault();
            }
          }
        }
      }
    };
  }

  /**
   * Create component.
   * @public
   */
  init() {
    this._getCloseButtons();
    this._getFocusableElements();
    this._addEventListeners();
  }

  /**
   * Destroy component.
   * @public
   */
  destroy() {
    this._removeEventListeners();
  }

  /**
   * Add event listeners.
   * @private
   */
  _addEventListeners() {
    if (this.trigger) {
      const button = document.querySelector(this.trigger);
      button.addEventListener('click', this.listeners.show);
    } else {
      this.dom.dialog.addEventListener('triggerDialog', this.listeners.triggerDialog);
    }

    this.dom.closeButtons.forEach((button) => {
      button.addEventListener('click', this.listeners.hide);
    });

    document.addEventListener('keydown', this.listeners.keydown);
  }

  /**
   * Remove event listeners.
   * @private
   */
  _removeEventListeners() {
    if (this.trigger) {
      const button = document.querySelector(this.trigger);
      button.removeEventListener('click', this.listeners.show);
    } else {
      this.dom.dialog.removeEventListener('triggerDialog', this.listeners.triggerDialog);
    }

    this.dom.closeButtons.forEach((button) => {
      button.removeEventListener('click', this.listeners.hide);
    });

    document.removeEventListener('keydown', this.listeners.keydown);
  }

  /**
   * Show dialog.
   * @public
   */
  show() {
    this.dom.dialog.classList.add('show');
    this.dom.dialog.dispatchEvent(this.events.showDialogEvent);
  }

  /**
   * Hide dialog.
   * @public
   */
  hide() {
    this.dom.dialog.classList.remove('show');
    this.dom.dialog.dispatchEvent(this.events.closeDialogEvent);
  }

  /**
   * Find elements to close the dialog (button or icon) and save them for future usage.
   * @private
   */
  _getCloseButtons() {
    const buttons = this.dom.dialog.querySelectorAll('[data-close]');
    for (const button of buttons) {
      this.dom.closeButtons.push(button);
    }
  }

  /**
   * Find focusable elements in the dialog and save them for future usage.
   * https://gist.github.com/drublic/5899658
   * @private
   */
  _getFocusableElements() {
    const focusableContent = `a[href], area[href], button:not([disabled]),
      input:not([disabled]), select:not([disabled]), textarea:not([disabled]),
      iframe, object, embed, *[tabindex]:not([tabindex="-1"]), *[contenteditable]`;
    this.dom.focusableElements = this.dom.dialog.querySelectorAll(focusableContent);
  }

  /**
   * Trigger dialog.
   * @private
   * @param {CustomEvent} evt - Event.
   */
  _triggerDialog(evt) {
    if (evt.detail.id === this.dom.id) {
      this.show();
    }
  }
}
